import Autorenew from "@mui/icons-material/Autorenew";
import Cancel from "@mui/icons-material/Cancel";
import InsertLink from "@mui/icons-material/InsertLink";
import Search from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  type FormControlProps,
  FormHelperText,
  Link,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash-es";
import { type ReactElement, useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import type { Client, UnsplashApi } from "unsplash-js";
import { useUnsplashSearch } from "../../../../server/api/unsplash";
import { useToken } from "../../../../server/auth/use-token";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import { getImageDetails } from "./service/get-image";

interface Props {
  onImageSelected: ({ url, downloadUrl }: { url?: string; downloadUrl?: string }) => void;
  url: string;
}

interface ImageInfo {
  url: string;
  downloadUrl?: string;
  pageUrl?: string;
  index: number;
}

type ExtractPaths<T> = T extends Client<infer P, never> ? P : never;
type UnsplashPaths = ExtractPaths<UnsplashApi>;
type Photo =
  UnsplashPaths["/search/photos"]["get"]["responses"]["200"]["content"]["application/json"];

export const ImageSelector = ({
  onImageSelected,
  url,
  disabled,
}: Props & FormControlProps): ReactElement => {
  const [image, setImage] = useState<ImageInfo | undefined>();
  const [sampleImages, setSampleImages] = useState<NonNullable<Photo["results"]>>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [imageDialog, setImageDialog] = useState<boolean>(false);
  const getToken = useToken();

  const { control, getValues } = useFormContext<MomentFormMoment>();
  useWatch({ control, name: "title" });
  const query = getValues("title");

  const unsplashSearch = useUnsplashSearch();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSampleImages([]);
  }, [query]);

  const handleNewImage = useCallback(async (): Promise<void> => {
    if (sampleImages.length === 0 || !image) {
      const { data, error } = await unsplashSearch(query, "landscape");
      if (error || isEmpty(data?.photos!.results)) {
        toast.error("No similar images to Communication name");
        return;
      }

      const images = data?.photos!.results ?? [];

      setSampleImages(images);
      const img = images[0]!;
      setImage({
        url: img.urls.small,
        index: 0,
        downloadUrl: img.links.download_location,
      });
      onImageSelected({ url: img.urls.small, downloadUrl: img.links.download_location });
    } else {
      const newIndex = (image?.index + 1) % sampleImages.length;
      const img = sampleImages[newIndex]!;

      setImage({ url: img.urls.small, index: newIndex, downloadUrl: img.links.download_location });
      onImageSelected({ url: img.urls.small, downloadUrl: img.links.download_location });
    }
  }, [query, image, sampleImages, unsplashSearch, onImageSelected]);

  const handleCancel = useCallback((): void => {
    setImage(undefined);
    onImageSelected({});
    setErrorMessage(undefined);
  }, [onImageSelected]);

  const validateURL = useCallback(
    async (newURL: string): Promise<boolean> => {
      setErrorMessage(undefined);

      if (!(
        newURL.startsWith("https://unsplash.com/") ||
        newURL.startsWith("https://images.unsplash.com/")
      )) {
        setErrorMessage("Only unsplash images are permitted");
        return false;
      }

      try {
        await getImageDetails(getToken, newURL);
      } catch {
        setErrorMessage("Invalid URL");
        return false;
      }

      return true;
    },
    [getToken],
  );

  const handleSelectImage = useCallback(async (): Promise<void> => {
    const pageUrl: string = image?.pageUrl || "";

    if (await validateURL(pageUrl)) {
      const { url: imageUrl, downloadUrl } = await getImageDetails(getToken, pageUrl);
      onImageSelected({ url: imageUrl, downloadUrl });

      setImage({ index: 0, url: imageUrl, pageUrl, downloadUrl });
      setSampleImages([]);
    }
  }, [image, onImageSelected, validateURL, getToken]);

  const handleCloseDialog = useCallback((): void => {
    setImageDialog(false);
    setErrorMessage(undefined);
  }, []);

  const handleSaveAndClose = useCallback(async (): Promise<void> => {
    await handleSelectImage();
    if (!errorMessage) {
      onImageSelected({ url: image?.url, downloadUrl: image?.downloadUrl });
      handleCloseDialog();
    }
  }, [handleSelectImage, errorMessage, image, onImageSelected, handleCloseDialog]);

  return (
    <Box>
      <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }} fullWidth>
        {url ? (
          <img
            alt={query}
            src={url}
            width={250}
            height={142}
            style={{ objectFit: "cover", objectPosition: "top" }}
          />
        ) : (
          <Box>
            <Skeleton
              sx={{ bgcolor: "grey.200" }}
              variant="rectangular"
              animation={false}
              width={250}
              height={142}
              id="placeholderImage"
            />
            <Typography
              sx={{
                color: "disabled.main",
                position: "absolute",
                top: "35%",
                left: "46%",
                transform: "translate(-50%, -50%)",
              }}
              variant="h5"
              textAlign="center"
            >
              THEME IMAGE
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
            width: "85%",
            mt: 2,
            ml: 2,
          }}
        >
          <Tooltip title="Generate image based on Communication" arrow placement="top">
            <Box component="span">
              <Button
                data-analytics-id="moment-form-image-generate"
                onClick={() => {
                  void handleNewImage().catch(console.error);
                }}
                disabled={disabled || !query}
                data-testid="searchButton"
              >
                {sampleImages.length > 0 ? <Autorenew /> : <Search />}
              </Button>
            </Box>
          </Tooltip>

          <Tooltip title="Link to image" arrow placement="top">
            <Box component="span">
              <Button
                data-analytics-id="moment-form-image-link"
                sx={{ ml: 2 }}
                onClick={() => setImageDialog(true)}
                data-testid="linkButton"
                disabled={disabled}
              >
                <InsertLink />
              </Button>
            </Box>
          </Tooltip>

          <Tooltip title="Reset" arrow placement="top">
            <Box component="span">
              <Button
                data-analytics-id="moment-form-image-reset"
                sx={{ ml: 2 }}
                onClick={handleCancel}
                disabled={disabled || !url}
                data-testid="cancelButton"
              >
                <Cancel />
              </Button>
            </Box>
          </Tooltip>
        </Box>
      </FormControl>

      <Dialog
        open={imageDialog}
        onClose={handleCloseDialog}
        aria-labelledby="image-dialog-title"
        aria-describedby="image-dialog-description"
      >
        <DialogTitle id="image-dialog-title">Link to image</DialogTitle>
        <DialogContent>
          <DialogContentText id="image-dialog-description">
            Add your&nbsp;
            <Link target="_blank" rel="noreferrer" href="https://www.unsplash.com">
              Unsplash
            </Link>
            &nbsp;image here
          </DialogContentText>

          <TextField
            label="Image url"
            value={image?.pageUrl}
            placeholder="https://unsplash.com/"
            sx={{ mt: 2 }}
            onBlur={() => {
              void handleSelectImage().catch(console.error);
            }}
            error={!!errorMessage}
            onChange={(event) => {
              setImage({ index: 0, url: "", pageUrl: event.target.value });
            }}
            fullWidth
          />
          {errorMessage && (
            <FormHelperText error variant="filled">
              {errorMessage}
            </FormHelperText>
          )}
          <FormHelperText variant="filled" sx={{ mt: 2 }}>
            To find the correct Unsplash image URL, head to their&nbsp;
            <Link target="_blank" rel="noreferrer" href="https://www.unsplash.com">
              website
            </Link>
            &nbsp;and search for the image you want and then paste the link here.
          </FormHelperText>
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id="moment-form-image-link-dialog-cancel"
            onClick={handleCloseDialog}
          >
            Cancel
          </Button>
          <Button
            data-analytics-id="moment-form-image-link-dialog-ok"
            variant="contained"
            onClick={() => {
              void handleSaveAndClose().catch(console.error);
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
